import {
    ArrayInput,
    AutocompleteInput,
      Create,
    DateInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    SimpleFormIterator,
    TextInput,
} from '@/components/admin';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { required, useNotify, useRedirect } from 'ra-core';
import { Link, useLocation } from 'react-router-dom';
import { DCAddressFields } from './DCAddressFields';
import { AutoPopulateCompanyDetails } from '../shared/AutoPopulateCompanyDetails';
import { AutoReferenceField } from '../shared/AutoReferenceField';
import { CHALLAN_TYPE_CHOICES, DELIVERY_TERMS_CHOICES, MODE_OF_TRANSPORT_CHOICES, UOM_CHOICES } from '../shared/companyConstants';
import { HSNFieldWrapper, HSNSuggestionsWrapper } from '../shared/HSNAutocomplete';
import { InventoryItemPickerWrapper } from '../shared/InventoryItemPicker';
import { FilteredContactInput } from '../../misc/FilteredContactInput';

export const DCCreate = () => {
    const location = useLocation();
    const sourceRecord = location.state?.record || {};
    const redirect = useRedirect();
    const notify = useNotify();

    const createTitle = (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/delivery_challans" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back to Delivery Challans
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight">New Delivery Challan</span>
        </div>
    );

    return (
        <Create
            record={sourceRecord}
            title={createTitle}
            mutationOptions={{
                onSuccess: (data: any) => {
                    notify("ra.notification.created", { type: "info", messageArgs: { smart_count: 1 } });
                    redirect(`/delivery_challans/${data.id}/show`);
                },
            }}
        >
            <SimpleForm>
<AutoPopulateCompanyDetails />
                {/* Reference + Date + Type */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full [&>*]:min-w-0">
                    <AutoReferenceField docType="delivery_challan" />
                    <DateInput source="challan_date" label="Challan Date" defaultValue={new Date()} />
                    <SelectInput source="challan_type" label="Challan Type" choices={CHALLAN_TYPE_CHOICES} defaultValue="Direct" />
                </div>

                {/* Transport */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full [&>*]:min-w-0">
                    <SelectInput source="mode_of_transport" label="Mode of Transport" choices={MODE_OF_TRANSPORT_CHOICES} />
                    <SelectInput source="delivery_terms" label="Delivery Terms" choices={DELIVERY_TERMS_CHOICES} helperText="Domestic transport or export/import (Incoterms)" />
                    <TextInput source="vehicle_no" label="Vehicle No." defaultValue="NA" />
                </div>

                {/* Customer Links */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full [&>*]:min-w-0">
                    <ReferenceInput source="company_id" reference="companies">
                        <AutocompleteInput label="Bill To Company" />
                    </ReferenceInput>
                    <FilteredContactInput label="Contact" />
                </div>

                {/* Contact No. override (X-3: removed dup "Contact Person Name" —
                    the "Contact" dropdown above already identifies the person) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full [&>*]:min-w-0">
                    <TextInput source="contact_no" label="Contact No. (override)" helperText="Leave blank to use the selected contact's number" />
                </div>

                {/* Project / PI link */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full [&>*]:min-w-0">
                    <ReferenceInput source="project_id" reference="projects">
                        <AutocompleteInput label="Linked Project (optional)" />
                    </ReferenceInput>
                    <ReferenceInput source="pi_id" reference="proforma_invoices">
                        <AutocompleteInput label="From Proforma Invoice (optional)" optionText="reference" />
                    </ReferenceInput>
                </div>

                {/* Ship To Address */}
        <DCAddressFields />

                {/* Items */}
                <div className="w-full mt-4">
                    <h3 className="font-semibold mb-2 text-sm">Items</h3>
                    <ArrayInput source="items" label="">
                        <SimpleFormIterator className="flex flex-col gap-4">
                            <div className="relative">
                                <InventoryItemPickerWrapper setTaxRate={false} priceField={null} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start w-full [&>*]:min-w-0">
                                <div className="md:col-span-2 relative">
                                    <TextInput source="description" label="Product Description" validate={required()} helperText={false} className="w-full" />
                                    <HSNSuggestionsWrapper />
                                </div>
                                <HSNFieldWrapper label="HSN / Item" />
                                <TextInput source="quantity" label="Quantity" helperText={false} />
                                <SelectInput source="unit" label="UoM" choices={UOM_CHOICES} defaultValue="Set" helperText={false} />
                            </div>
                            {/* Sub-items */}
                            <div className="ml-4 border-l-2 border-slate-200 pl-4">
                                <p className="text-xs text-muted-foreground mb-2">Sub-items (optional — numbered list within this product)</p>
                                <ArrayInput source="sub_items" label="">
                                    <SimpleFormIterator>
                                        <TextInput source="text" label="Sub-item description" helperText={false} className="w-full" />
                                        <TextInput source="qty_note" label="Total qty note (e.g. 80)" helperText={false} className="w-40" />
                                    </SimpleFormIterator>
                                </ArrayInput>
                            </div>
                        </SimpleFormIterator>
                    </ArrayInput>
                </div>
            </SimpleForm>
        </Create>
    );
};
